import {api} from './api';
import {Entry} from '../types';
import { RootState } from '../store';
import {
	entriesAddMany,
	entriesSetState,
	entriesUpdateMany,
} from '../containers/contentSlice';

const noteTagId = (noteId: string) => `note:${noteId}`;

export const recordsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		loadEntry: builder.query({
			query: (id) => ({
				url: id ? `records/${id}` : 'records',
				method: 'GET',
			}),
			providesTags: ['BoardElements'],
		}),
		entryCreate: builder.mutation({
			query: (entries: Entry[]) => ({
				url: 'records',
				method: 'POST',
				body: {
					records: entries
				},
			}) as any,
			async onQueryStarted(
				arg: Entry[],
				{
					dispatch,
					queryFulfilled,
				}: any
			) {
				dispatch(entriesAddMany(arg));
				try {
					await queryFulfilled;
					const noteParentIdsToInvalidate = new Set<string>();
					for (const entry of arg) {
						if (entry.type !== 'note') {
							continue;
						}
						const parentId = String(entry.parentId ?? '');
						if (parentId) {
							noteParentIdsToInvalidate.add(parentId);
						}
					}
					if (noteParentIdsToInvalidate.size) {
						dispatch(api.util.invalidateTags(
							Array.from(noteParentIdsToInvalidate).map((parentId) => ({
								type: 'Notes' as const,
								id: noteTagId(parentId),
							})),
						));
					}
				} catch {
					// no-op
				}
			},
		} as any),
		entryImport: builder.mutation({
			query: (entries: Entry[]) => ({
				url: 'records/import',
				method: 'POST',
				body: {
					records: entries
				},
			}) as any,
			async onQueryStarted(
				arg: Entry[],
				{
					dispatch,
					queryFulfilled,
					getState,
				}: any
			) {
				const isAuthenticated = Boolean((getState() as RootState).auth.user);
				if (!isAuthenticated) {
					dispatch(entriesAddMany(arg));
				}
				try {
					await queryFulfilled;
					if (!isAuthenticated) {
						return;
					}
					// Import assigns server UUIDs; optimistic preset ids must not remain in Redux.
					dispatch(entriesSetState([]));
					await dispatch(
						recordsApi.endpoints.loadEntry.initiate(null, {
							forceRefetch: true,
							subscribe: false,
						}),
					).unwrap();
				} catch {
					// no-op
				}
			},
			invalidatesTags: ['Repositories', 'RepositoryEntries', 'RepositoryEntryCount'],
			extraOptions: { maxRetries: 0 },
		} as any),
		entryUpdate: builder.mutation({
			query: (entries: Partial<Entry>[]) => ({
				url: 'records',
				method: 'PUT',
				body: {
					records: entries
				},
			}),
			async onQueryStarted(entries: Partial<Entry>[], { dispatch, getState, queryFulfilled }: any) {
				const allEntries = (getState() as RootState).content.entries;
				const previousParentIdByEntryId = new Map<string, string>();
				for (const update of entries) {
					const nextParentIdRaw = update.parentId;
					if (!update.id) {
						continue;
					}
					const existing = allEntries.find((entry) => entry.id === update.id);
					if (nextParentIdRaw === undefined) {
						continue;
					}
					if (existing?.type !== 'note') {
						continue;
					}
					previousParentIdByEntryId.set(
						update.id,
						String(existing.parentId ?? ''),
					);
				}
				dispatch(entriesUpdateMany(entries));
				try {
					await queryFulfilled;
					const noteParentIdsToInvalidate = new Set<string>();
					for (const update of entries) {
						if (!update.id) {
							continue;
						}
						const oldParentId = previousParentIdByEntryId.get(update.id) ?? '';
						const newParentId = String(update.parentId ?? '');
						if (oldParentId) {
							noteParentIdsToInvalidate.add(oldParentId);
						}
						if (newParentId) {
							noteParentIdsToInvalidate.add(newParentId);
						}
					}
					if (noteParentIdsToInvalidate.size) {
						dispatch(api.util.invalidateTags(
							Array.from(noteParentIdsToInvalidate).map((parentId) => ({
								type: 'Notes' as const,
								id: noteTagId(parentId),
							})),
						));
					}
				} catch {
					// no-op
				}
			},
		}),
		/** Lightweight xy-only updates for canvas dragging; avoid heavy cache invalidation churn. */
		entryReposition: builder.mutation({
			query: (entries: Partial<Entry>[]) => ({
				url: 'records',
				method: 'PUT',
				body: {
					records: entries
				},
			}),
			async onQueryStarted(entries: Partial<Entry>[], { dispatch }: any) {
				dispatch(entriesUpdateMany(entries));
			},
		}),
		entryDelete: builder.mutation({
			query: ({ id }: { id: string }) => ({
				url: `records/${id}`,
				method: 'DELETE',
			}),
			async onQueryStarted({ id }: { id: string }, { dispatch, getState }: any) {
				const allEntries = (getState() as RootState).content.entries as Entry[];
				const deletedEntry = allEntries.find((entry) => entry.id === id);
				const parentId = String(deletedEntry?.parentId ?? '');
				if (parentId) {
					dispatch(
						api.util.invalidateTags([
							{ type: 'Notes' as const, id: noteTagId(parentId) },
						]),
					);
				}
			},
		}),
	}),
});

export const {
	useLoadEntryQuery,
	useEntryCreateMutation,
	useEntryUpdateMutation,
	useEntryRepositionMutation,
	useEntryDeleteMutation,
	useEntryImportMutation,
} = recordsApi;
